import { useState, useEffect, useRef, useCallback } from "react";
import {
  getServers, getServerRmmSummary, getClients, getAgents, updateServer,
  getServerCandidates, promoteToServer, demoteServer, getServerInstallCommand,
} from "../api";
import {
  Server, Activity, WifiOff, RefreshCw, Wifi, Cpu, HardDrive, Clock,
  Plus, X, User, Terminal, MonitorSmartphone, Search, Copy, Check, Download,
} from "lucide-react";
import { tokens, sx, FullPageSpinner } from "../theme";
import useIsMobile from "../hooks/useIsMobile";

// Glass hexagon KPI icon — matches the Devices page aesthetic.
const HEX_CLIP = "polygon(25% 5%,75% 5%,100% 50%,75% 95%,25% 95%,0 50%)";
function KpiHex({ icon: Icon, color }) {
  return (
    <div style={{
      position:"relative", width:42, height:42, flexShrink:0, clipPath:HEX_CLIP,
      display:"flex", alignItems:"center", justifyContent:"center", background:color,
      filter:`drop-shadow(0 0 7px ${color}8c) drop-shadow(0 3px 5px rgba(0,0,0,0.3))`,
    }}>
      <div style={{
        position:"absolute", inset:1.5, clipPath:HEX_CLIP,
        background:`radial-gradient(circle at 34% 24%, rgba(255,255,255,0.16), transparent 60%), linear-gradient(157deg, ${color}4d, ${color}0f), #0a1326`,
      }}/>
      <Icon size={19} color={color} style={{ position:"relative", zIndex:2, filter:`drop-shadow(0 0 5px ${color}8c)` }}/>
    </div>
  );
}

const KPI = [
  { key:"total",   label:"Servers",  icon:Server,   color:"#4f6fff" },
  { key:"online",  label:"Online",   icon:Activity, color:"#10b981" },
  { key:"linux",   label:"Linux",    icon:Terminal, color:"#22d3ee" },
  { key:"windows", label:"Windows",  icon:MonitorSmartphone, color:"#7b61ff" },
  { key:"offline", label:"Offline",  icon:WifiOff,  color:"#8d99ae" },
];

// ── Helpers ───────────────────────────────────────────────────
function timeSince(dateStr) {
  if (!dateStr) return "Never";
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function uptimeStr(hours) {
  if (hours == null) return null;
  const h = Number(hours);
  if (isNaN(h)) return null;
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d`;
}
function getMetrics(d) {
  return {
    cpu:       d.cpu_percent ?? d.cpu_load_percent ?? null,
    ram:       d.ram_percent ?? d.ram_used_percent ?? null,
    disk:      d.disk_used_percent ?? null,
    ramUsed:   d.health_ram_used_gb ?? d.ram_used_gb ?? null,
    ramTotal:  d.health_ram_total_gb ?? d.ram_total_gb ?? null,
    diskUsed:  d.disk_used_gb ?? null,
    diskTotal: d.disk_total_gb ?? null,
  };
}

const OS_BADGE = {
  linux:   { label:"Linux",   color:"#22d3ee", bg:"rgba(34,211,238,0.12)", border:"rgba(34,211,238,0.3)" },
  windows: { label:"Windows", color:"#a78bfa", bg:"rgba(123,97,255,0.14)", border:"rgba(123,97,255,0.32)" },
  bsd:     { label:"BSD",     color:"#f87171", bg:"rgba(248,113,113,0.12)", border:"rgba(248,113,113,0.3)" },
  macos:   { label:"macOS",   color:"#cbd5e1", bg:"rgba(203,213,225,0.12)", border:"rgba(203,213,225,0.3)" },
};

const statusConfig = {
  online:   { color:"#10b981", bg:"#052e16", label:"Online"   },
  warning:  { color:"#f59e0b", bg:"#3f3006", label:"Warning"  },
  critical: { color:"#ef4444", bg:"#450a0a", label:"Critical" },
  offline:  { color:"var(--t-text-label)", bg:"#1f2937", label:"Offline"  },
  unknown:  { color:"var(--t-text-label)", bg:"#1f2937", label:"Unknown"  },
};

// ── Compact metric tile ───────────────────────────────────────
function SStat({ label, value, sub, icon: Icon }) {
  const v = value == null || isNaN(value) ? null : Math.round(value);
  const color = v == null ? "var(--t-text-faint)" : v >= 90 ? "#f87171" : v >= 75 ? "#fbbf24" : "#34d399";
  return (
    <div style={{ background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)", borderRadius:9, padding:"9px 10px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", marginBottom:5 }}>
        <span style={{ fontSize:9, fontWeight:700, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.05em", display:"flex", alignItems:"center", gap:4 }}>
          {Icon && <Icon size={10}/>}{label}
        </span>
        <span style={{ fontSize:13, fontWeight:700, color }}>{v == null ? "—" : `${v}%`}</span>
      </div>
      <div style={{ height:4, background:"rgba(148,163,184,0.12)", borderRadius:99, overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${v || 0}%`, background:color, borderRadius:99, transition:"width 0.3s" }}/>
      </div>
      {sub && <div style={{ fontSize:9, color:"var(--t-text-deep)", marginTop:4, textAlign:"right" }}>{sub}</div>}
    </div>
  );
}

// ── Assignment row (client / contact / staff / server role) ────
function ServerAssign({ server, clients, agents, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const change = async (field, value) => {
    setSaving(true);
    try { await updateServer(server.id, { [field]: value || null }); onUpdate(); }
    catch (e) { console.error(e); } finally { setSaving(false); }
  };
  const sel = {
    background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:6,
    padding:"5px 8px", color:"var(--t-text-primary)", fontSize:11, cursor:"pointer",
    width:"100%", opacity: saving ? 0.6 : 1,
  };
  return (
    <div style={{ marginBottom:12, display:"flex", flexDirection:"column", gap:6 }}>
      <div style={{ fontSize:10, fontWeight:700, color:"var(--t-text-deep)", textTransform:"uppercase", letterSpacing:"0.05em", display:"flex", alignItems:"center", gap:4 }}>
        <User size={10}/> Assignment
      </div>
      <select value={server.client_id || ""} onChange={e => change("client_id", e.target.value)} style={sel} disabled={saving}>
        <option value="">Client — Unassigned</option>
        {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
      </select>
      <select value={server.agent_id || ""} onChange={e => change("agent_id", e.target.value)} style={sel} disabled={saving}>
        <option value="">Owner (staff) — Unassigned</option>
        {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name} ({a.role})</option>)}
      </select>
    </div>
  );
}

// ── Add-Server modal: install a new Linux server, or promote a device ──
function AddServerModal({ onClose, onAdded }) {
  const [tab, setTab]               = useState("install"); // install | promote
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [q, setQ]                   = useState("");
  const [promoting, setPromoting]   = useState(null);
  const [cmd, setCmd]               = useState(null);
  const [copied, setCopied]         = useState(false);
  const [copiedWin, setCopiedWin]   = useState(false);

  useEffect(() => {
    getServerInstallCommand().then(r => setCmd(r.data)).catch(() => {});
    getServerCandidates().then(r => setCandidates(Array.isArray(r.data) ? r.data : []))
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const promote = async (id) => {
    setPromoting(id);
    try { await promoteToServer(id); onAdded(); onClose(); }
    catch (e) { console.error(e); alert("Could not promote device"); }
    finally { setPromoting(null); }
  };

  const copy = () => {
    if (!cmd?.linux) return;
    navigator.clipboard?.writeText(cmd.linux).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); });
  };
  const copyWin = () => {
    if (!cmd?.windows) return;
    navigator.clipboard?.writeText(cmd.windows).then(() => { setCopiedWin(true); setTimeout(() => setCopiedWin(false), 1800); });
  };

  const filtered = candidates.filter(c => {
    if (!q) return true;
    const hay = [c.device_name, c.computer_name, c.os, c.os_version, c.client_name].join(" ").toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} style={{
      flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:7, padding:"10px 12px",
      background: tab === id ? "rgba(79,111,255,0.12)" : "transparent", border:"none",
      borderBottom: tab === id ? "2px solid #4f6fff" : "2px solid transparent",
      color: tab === id ? "#818cf8" : "var(--t-text-faint)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
    }}><Icon size={14}/> {label}</button>
  );

  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(2,6,23,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...sx.card, width:"100%", maxWidth:600, maxHeight:"82vh", display:"flex", flexDirection:"column", overflow:"hidden" }}>
        <div style={{ ...sx.cardHead, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span>Add a Server</span>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", display:"flex" }}><X size={16}/></button>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${tokens.border.soft}` }}>
          <TabBtn id="install" icon={Download} label="Install on a server"/>
          <TabBtn id="promote" icon={Server} label="Promote a device"/>
        </div>

        {tab === "install" ? (
          <div style={{ padding:16, overflowY:"auto" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:700, fontSize:13, color:"var(--t-text-primary)", marginBottom:8 }}>
              <Terminal size={15} color="#22d3ee"/> Linux server
            </div>
            <div style={{ fontSize:12, color:"var(--t-text-faint)", lineHeight:1.5, marginBottom:12 }}>
              Run this one-liner on the server (as root). It installs the lightweight agent as a systemd service; the server appears here within ~60s. The agent is outbound-only — no inbound ports required.
            </div>
            <div style={{ position:"relative", background:"#0a1326", border:"1px solid rgba(79,111,255,0.3)", borderRadius:10, padding:"12px 44px 12px 14px", fontFamily:"monospace", fontSize:11.5, color:"#cdd9ef", wordBreak:"break-all", lineHeight:1.5 }}>
              {cmd?.linux || "Loading…"}
              <button onClick={copy} title="Copy" style={{ position:"absolute", top:8, right:8, background:"rgba(79,111,255,0.15)", border:"1px solid rgba(79,111,255,0.3)", borderRadius:6, padding:6, color: copied ? "#34d399" : "#818cf8", cursor:"pointer", display:"flex" }}>
                {copied ? <Check size={13}/> : <Copy size={13}/>}
              </button>
            </div>
            <div style={{ marginTop:16, paddingTop:14, borderTop:`1px solid ${tokens.border.soft}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:700, fontSize:13, color:"var(--t-text-primary)", marginBottom:8 }}>
                <MonitorSmartphone size={15} color="#a78bfa"/> Windows server
              </div>
              <div style={{ fontSize:12, color:"var(--t-text-faint)", lineHeight:1.5, marginBottom:12 }}>
                {cmd?.windows_note || "Run in an elevated PowerShell (Run as administrator) on the Windows server. Installs only the RMM agent + remote access — no ticketing app. The server appears here within ~60s."}
              </div>
              <div style={{ position:"relative", background:"#0a1326", border:"1px solid rgba(167,139,250,0.35)", borderRadius:10, padding:"12px 44px 12px 14px", fontFamily:"monospace", fontSize:11.5, color:"#cdd9ef", whiteSpace:"pre-wrap", wordBreak:"break-word", lineHeight:1.55 }}>
                {cmd?.windows || "Loading…"}
                <button onClick={copyWin} title="Copy" style={{ position:"absolute", top:8, right:8, background:"rgba(167,139,250,0.15)", border:"1px solid rgba(167,139,250,0.35)", borderRadius:6, padding:6, color: copiedWin ? "#34d399" : "#a78bfa", cursor:"pointer", display:"flex" }}>
                  {copiedWin ? <Check size={13}/> : <Copy size={13}/>}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ padding:14, borderBottom:`1px solid ${tokens.border.soft}` }}>
              <div style={{ fontSize:11, color:"var(--t-text-faint)", marginBottom:10 }}>
                Already managing this machine as a device? Flag it as a server instead.
              </div>
              <div style={{ position:"relative" }}>
                <Search size={13} style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", color:"var(--t-text-faint)" }}/>
                <input placeholder="Search devices..." value={q} onChange={e => setQ(e.target.value)} style={{ ...sx.input, paddingLeft:30 }}/>
              </div>
            </div>
            <div style={{ overflowY:"auto", padding:8 }}>
              {loading ? <div style={{ padding:30 }}><FullPageSpinner/></div> :
                filtered.length === 0 ? <div style={{ padding:30, textAlign:"center", color:"var(--t-text-deep)", fontSize:13 }}>No eligible devices found.</div> :
                filtered.map(c => (
                  <div key={c.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 10px", borderRadius:8, marginBottom:4, background:"var(--t-surface-deep)", border:"1px solid var(--t-border-soft)" }}>
                    <Server size={15} color="#4f6fff" style={{ flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"var(--t-text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.device_name || c.computer_name}</div>
                      <div style={{ fontSize:10, color:"var(--t-text-faint)" }}>{[c.os, c.client_name].filter(Boolean).join(" · ")} · {timeSince(c.last_seen)}</div>
                    </div>
                    <button onClick={() => promote(c.id)} disabled={promoting === c.id} style={{ ...sx.buttonPrimary, padding:"6px 12px", fontSize:11 }}>
                      {promoting === c.id ? "Adding..." : "Make Server"}
                    </button>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
export default function Servers() {
  const [servers,   setServers]   = useState([]);
  const [summary,   setSummary]   = useState({});
  const [clients,   setClients]   = useState([]);
  const [agents,    setAgents]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState("");
  const [filter,    setFilter]    = useState({ client_id:"", os_family:"", status:"" });
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(30);
  const [showAdd,   setShowAdd]   = useState(false);
  const [confirmDemote, setConfirmDemote] = useState(null);
  const intervalRef  = useRef(null);
  const countdownRef = useRef(null);
  const isMobile     = useIsMobile();

  const fetchServers = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [s, sum] = await Promise.all([getServers({ client_id: filter.client_id }), getServerRmmSummary()]);
      setServers(Array.isArray(s.data) ? s.data : []);
      setSummary(sum.data || {});
      setLastRefresh(new Date());
      setCountdown(30);
    } catch (e) { console.error(e); }
    finally { if (!silent) setLoading(false); }
  }, [filter.client_id]);

  useEffect(() => { fetchServers(); }, [fetchServers]);
  useEffect(() => {
    Promise.all([getClients(), getAgents()]).then(([cl, ag]) => {
      setClients(Array.isArray(cl.data) ? cl.data : []);
      setAgents(Array.isArray(ag.data) ? ag.data : []);
    }).catch(console.error);
  }, []);
  useEffect(() => {
    intervalRef.current  = setInterval(() => fetchServers(true), 30000);
    countdownRef.current = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 30), 1000);
    return () => { clearInterval(intervalRef.current); clearInterval(countdownRef.current); };
  }, [fetchServers]);

  const demote = async (id) => {
    try { await demoteServer(id); setServers(prev => prev.filter(s => s.id !== id)); setConfirmDemote(null); }
    catch (e) { console.error(e); }
  };

  const filtered = servers.filter(d => {
    if (filter.status && d.status !== filter.status) return false;
    if (filter.os_family && d.os_family !== filter.os_family) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [d.device_name, d.computer_name, d.client_name, d.os, d.os_version, d.server_role, d.agent_name];
      if (!hay.some(v => (v || "").toString().toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const isActive = (d) => d.status === "online" || (d.last_seen && Date.now() - new Date(d.last_seen).getTime() < 5 * 60_000);
  const sorted = [...filtered].sort((a, b) => {
    const aAct = isActive(a) ? 1 : 0, bAct = isActive(b) ? 1 : 0;
    if (bAct !== aAct) return bAct - aAct;
    const aSeen = a.last_seen ? new Date(a.last_seen).getTime() : 0;
    const bSeen = b.last_seen ? new Date(b.last_seen).getTime() : 0;
    if (bSeen !== aSeen) return bSeen - aSeen;
    return (a.device_name || "").localeCompare(b.device_name || "");
  });

  const inputStyle = { ...sx.input, fontSize:13, padding:"8px 12px" };

  return (
    <div style={{ padding: isMobile ? 14 : 24, maxWidth:1400 }}>

      {/* Page head */}
      <div style={{ paddingBottom:14, borderBottom:`1px solid ${tokens.border.soft}`, marginBottom:18, display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:tokens.text.primary, letterSpacing:"-0.5px", display:"flex", alignItems:"center", gap:10 }}>
            <Server size={22} color={tokens.accent.indigo}/> Server RMM
          </h1>
          <p style={{ color:tokens.text.faint, fontSize:13, marginTop:3 }}>
            {summary.total ?? 0} servers managed
            {lastRefresh && ` · Updated ${lastRefresh.toLocaleTimeString()}`}
            {` · Refreshing in ${countdown}s`}
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={() => { fetchServers(); setCountdown(30); }} style={{ ...sx.buttonGhost, padding:"8px 14px" }}>
            <RefreshCw size={14}/> Refresh
          </button>
          <button onClick={() => setShowAdd(true)} style={{ ...sx.buttonPrimary, padding:"8px 14px" }}>
            <Plus size={14}/> Add Server
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display:"grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(5,1fr)", gap:10, marginBottom:18 }}>
        {KPI.map(({ key, label, icon, color }) => (
          <div key={key} style={{
            background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md,
            padding:"13px 15px", position:"relative", overflow:"hidden", boxShadow:tokens.shadow.panel,
            transition:"all 0.18s", display:"flex", alignItems:"center", gap:13,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.borderColor="var(--t-border-mid)"; e.currentTarget.style.boxShadow="var(--t-shadow-panel-hi)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)"; e.currentTarget.style.borderColor=tokens.border.soft; e.currentTarget.style.boxShadow=tokens.shadow.panel; }}>
            <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:tokens.shadow.cardSheen }}/>
            <KpiHex icon={icon} color={color}/>
            <div>
              <div style={{ fontSize:24, fontWeight:800, letterSpacing:-1, lineHeight:1, color: key==="total" ? tokens.text.primary : color }}>{summary[key] ?? 0}</div>
              <div style={{ fontSize:10, color:tokens.text.faint, marginTop:5, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
        <input placeholder="🔍 Search server, client, role..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...inputStyle, flex: isMobile ? "1 1 100%" : 1, minWidth:200 }}/>
        <select value={filter.client_id} onChange={e => setFilter(p=>({...p,client_id:e.target.value}))} style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        <select value={filter.os_family} onChange={e => setFilter(p=>({...p,os_family:e.target.value}))} style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">All OS</option>
          <option value="linux">Linux</option>
          <option value="windows">Windows</option>
          <option value="bsd">BSD</option>
        </select>
        <select value={filter.status} onChange={e => setFilter(p=>({...p,status:e.target.value}))} style={{ ...inputStyle, cursor:"pointer" }}>
          <option value="">All Statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="warning">Warning</option>
          <option value="critical">Critical</option>
        </select>
        {(filter.status || filter.client_id || filter.os_family || search) && (
          <button onClick={() => { setFilter({ client_id:"", os_family:"", status:"" }); setSearch(""); }}
            style={{ ...inputStyle, cursor:"pointer", background:tokens.surface.panel, color:tokens.text.faint, border:`1px solid ${tokens.border.soft}` }}>Clear</button>
        )}
      </div>

      {/* Grid */}
      {loading ? <FullPageSpinner/> :
        sorted.length === 0 ? (
          <div style={{ textAlign:"center", padding:60, color:tokens.text.deep, fontSize:14 }}>
            {search ? `No servers found matching "${search}"` : (
              <>
                <Server size={40} color={tokens.border.strong} style={{ marginBottom:12 }}/>
                <div>No servers yet. Click <strong>Add Server</strong> to promote a managed device.</div>
              </>
            )}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(330px,1fr))", gap:14 }}>
            {sorted.map(d => {
              const cfg = statusConfig[d.status] || statusConfig.unknown;
              const os  = OS_BADGE[d.os_family] || null;
              const m   = getMetrics(d);
              const name = d.device_name || d.computer_name || "Unknown";
              const up   = uptimeStr(d.uptime_hours);
              return (
                <div key={d.id}
                  onClick={(e) => { if (e.target.closest('button, select, input, a')) return; window.location.href = `/server-rmm/${encodeURIComponent(d.id)}`; }}
                  style={{ background:tokens.surface.panel, border:`1px solid ${tokens.border.soft}`, borderRadius:tokens.radius.md, padding:16, cursor:"pointer", boxShadow:tokens.shadow.panel, transition:"all 0.18s" }}
                  onMouseOver={x => { x.currentTarget.style.transform="translateY(-1px)"; x.currentTarget.style.borderColor="rgba(99,102,241,0.25)"; }}
                  onMouseOut={x => { x.currentTarget.style.transform="translateY(0)"; x.currentTarget.style.borderColor=tokens.border.soft; }}>
                  {/* Header */}
                  <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:13 }}>
                    <div style={{ width:44, height:44, borderRadius:12, flexShrink:0, position:"relative", overflow:"hidden",
                      background:"linear-gradient(135deg, rgba(79,111,255,0.18), rgba(123,97,255,0.18))", border:"1px solid rgba(79,111,255,0.28)",
                      display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 18px rgba(79,111,255,0.18), inset 0 1px 0 rgba(255,255,255,0.12)" }}>
                      <Server size={21} color="#22d3ee" style={{ position:"relative", zIndex:1, filter:"drop-shadow(0 0 6px rgba(0,212,255,0.5))" }}/>
                      <span style={{ position:"absolute", top:5, right:5, width:7, height:7, borderRadius:"50%", background:cfg.color, boxShadow:`0 0 6px ${cfg.color}`, zIndex:2 }}/>
                      <div style={{ position:"absolute", inset:0, background:"radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18), transparent 55%)", pointerEvents:"none" }}/>
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                        <span style={{ fontWeight:700, fontSize:15, color:"var(--t-text-primary)", letterSpacing:"-0.2px", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{name}</span>
                        <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:9, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", padding:"3px 8px", borderRadius:99, background:cfg.bg, color:cfg.color, flexShrink:0 }}>
                          <span style={{ width:6, height:6, borderRadius:"50%", background:cfg.color, boxShadow:`0 0 6px ${cfg.color}` }}/>{cfg.label}
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:"var(--t-text-faint)", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                        {os && <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:99, background:os.bg, color:os.color, border:`1px solid ${os.border}` }}>{os.label}</span>}
                        {d.server_role && <span style={{ color:"var(--t-text-deep)" }}>{d.server_role}</span>}
                        <span style={{ display:"inline-flex", alignItems:"center", gap:4 }}>{d.status==="offline" ? <WifiOff size={10}/> : <Wifi size={10}/>}{timeSince(d.last_seen)}</span>
                      </div>
                      <div style={{ fontSize:11, color:"var(--t-text-deep)", marginTop:2, display:"flex", gap:10, flexWrap:"wrap" }}>
                        {d.client_name && <span style={{ color:"#f59e0b" }}>{d.client_name}</span>}
                        {up && <span style={{ display:"inline-flex", alignItems:"center", gap:3 }}><Clock size={10}/> up {up}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Stat tiles */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:12 }}>
                    <SStat label="CPU"  value={m.cpu}  icon={Cpu}/>
                    <SStat label="RAM"  value={m.ram}  sub={m.ramUsed != null && m.ramTotal != null ? `${Number(m.ramUsed).toFixed(1)}/${Number(m.ramTotal).toFixed(0)}G` : null}/>
                    <SStat label="Disk" value={m.disk} icon={HardDrive} sub={m.diskUsed != null && m.diskTotal != null ? `${Number(m.diskUsed).toFixed(0)}/${Number(m.diskTotal).toFixed(0)}G` : null}/>
                  </div>

                  <ServerAssign server={d} clients={clients} agents={agents} onUpdate={() => fetchServers(true)}/>

                  {/* Actions */}
                  <button
                    onClick={() => window.location.href = `/server-rmm/${encodeURIComponent(d.id)}`}
                    style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"9px 10px", borderRadius:10,
                      background:"rgba(79,111,255,0.08)", color:"var(--btn-remote-text, #cdd9ef)", fontSize:12, fontWeight:600,
                      border:"1px solid rgba(79,111,255,0.25)", cursor:"pointer", transition:"all 0.2s ease", boxShadow:"inset 0 1px 0 rgba(255,255,255,0.05)" }}
                    onMouseEnter={e => { e.currentTarget.style.background="rgba(79,111,255,0.18)"; e.currentTarget.style.borderColor="rgba(79,111,255,0.6)"; e.currentTarget.style.boxShadow="0 0 18px rgba(79,111,255,0.25)"; e.currentTarget.style.color="#fff"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="rgba(79,111,255,0.08)"; e.currentTarget.style.borderColor="rgba(79,111,255,0.25)"; e.currentTarget.style.boxShadow="inset 0 1px 0 rgba(255,255,255,0.05)"; e.currentTarget.style.color="var(--btn-remote-text, #cdd9ef)"; }}>
                    Manage Server →
                  </button>

                  {/* Demote */}
                  {confirmDemote === d.id ? (
                    <div style={{ marginTop:10, padding:"10px 12px", background:"#0f0a0a", border:"1px solid #7f1d1d", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      <span style={{ fontSize:12, color:"#fca5a5" }}>Remove from servers?</span>
                      <div style={{ display:"flex", gap:6 }}>
                        <button onClick={() => setConfirmDemote(null)} style={{ padding:"5px 10px", background:"var(--t-surface-mid)", border:"1px solid var(--t-border-strong)", borderRadius:6, color:"var(--t-text-muted)", fontSize:11, cursor:"pointer" }}>Cancel</button>
                        <button onClick={() => demote(d.id)} style={{ padding:"5px 10px", background:"#b91c1c", border:"none", borderRadius:6, color:"#fff", fontSize:11, fontWeight:600, cursor:"pointer" }}>Remove</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDemote(d.id)}
                      style={{ marginTop:8, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:6, padding:"7px", background:"none", border:"1px solid var(--t-border-soft)", borderRadius:10, color:"var(--t-text-faint)", fontSize:11, fontWeight:500, cursor:"pointer", transition:"all 0.2s ease" }}
                      onMouseEnter={e => { e.currentTarget.style.color="#ff8a9b"; e.currentTarget.style.borderColor="rgba(255,84,112,0.4)"; }}
                      onMouseLeave={e => { e.currentTarget.style.color="var(--t-text-faint)"; e.currentTarget.style.borderColor=tokens.border.soft; }}>
                      Remove from Server RMM
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

      {showAdd && <AddServerModal onClose={() => setShowAdd(false)} onAdded={() => fetchServers()}/>}
    </div>
  );
}
